import { database } from '../../components/shared/database'
import { setDeviceServiceHistoryAction } from '../../reducers/actions/devices'
import { generateUUID } from '../../components/shared/uuid-generator'
import store from '../../store'
import { processQuerySnapshot } from '../../components/shared/process-query-snapshot'

interface DeviceEntity {
    uid: string
    [key: string]: unknown
}

interface ServiceHistoryEntry {
    uid?: string
    description?: string
    deviceUid?: string
    createdAt?: string
    createdBy?: string
    images?: string[]
    showForAll?: boolean
    [key: string]: unknown
}

interface ServiceHistoryToSave extends ServiceHistoryEntry {
    uid: string
    deviceUid: string
    createdAt: string
    createdBy: string
}

/**
 * Listen device service history from database
 *
 * @param {string} deviceUid
 * @return {Promise}
 */
export const readDeviceServiceHistory = (deviceUid: string): Promise<void> => {
    const authedUser = store.getState().auth.user as { superAdmin?: boolean } | null

    return database('device_service_history')
        .where('deviceUid', '==', deviceUid)
        .where('showForAll', !authedUser?.superAdmin ? '==' : 'in', !authedUser?.superAdmin ? true : [true, false])
        .orderBy('createdAt', 'desc')
        .get()
        .then(processQuerySnapshot)
        .then(resultData => setDeviceServiceHistoryAction(resultData))
}

/**
 * Save new device service history
 *
 * @param {Object} device
 * @param {Object} deviceServiceHistory
 * @return {Promise}
 */
export const saveDeviceServiceHistory = (
    device: DeviceEntity,
    deviceServiceHistory: ServiceHistoryEntry,
): Promise<ServiceHistoryToSave | void> => {
    if (!deviceServiceHistory.description || deviceServiceHistory.description === '') {
        return new Promise(resolve => resolve())
    }

    const serviceHistoryToSave: ServiceHistoryToSave = {
        ...deviceServiceHistory,
        uid: deviceServiceHistory.uid || generateUUID(),
        deviceUid: device.uid,
        createdAt: (window as Window & { moment?: { utc: () => { format: () => string } } }).moment?.utc().format() || new Date().toISOString(),
        createdBy: (store.getState().auth.user as { uid: string }).uid,
    }

    return database('device_service_history')
        .doc(serviceHistoryToSave.uid)
        .set(serviceHistoryToSave)
        .then(() => new Promise<ServiceHistoryToSave>(resolve => resolve(serviceHistoryToSave)))
}

/**
 * Save images into device service history
 *
 * @param {Object} deviceServiceHistory
 * @param {Array} images
 * @return {Promise}
 */
export const saveDeviceServiceHistoryImages = (
    deviceServiceHistory: ServiceHistoryToSave | null | undefined,
    images: string[],
): Promise<void> => {
    if (!deviceServiceHistory) {
        return new Promise(resolve => resolve())
    }

    return database('device_service_history')
        .doc(deviceServiceHistory.uid)
        .update({
            images: images,
        })
}
